import { useCallback, useMemo, useRef, useState } from "react";

export interface UseRowSelectionManager<T extends { id: string }> {
  selectedRowsHash: Record<string, T>;
  setSelectedRowsHash: any /* The typing for React setters is messy. */;
  selectRow: (locator: string, item: T) => void;
  deselectRow: (locator: string) => void;
  isRowSelected: (locator: string) => boolean;
  onRowSelectChange: (locator: string, isSelected: boolean, item: T) => void;
  selectAllRows: (rowData: T[]) => void;
  selectedRowCount: number;
  clearSelectedRows: () => void;
  isSelecting: boolean;
  setSelectionForVisible: (selected: boolean) => void;
  areAllVisibleRowsSelected: () => boolean;
  hasVisibleRows: boolean;
}

export interface UseRowSelectionManagerProps<T extends { id: string }> {
  selectionExcluder?: (row: T) => boolean;
  getVisibleRows?: () => { locator: string; item: T }[];
}

/**
 * A generic selection manager for a list of objects that have an `id` field (in our case, this ID is usually the public
 * ID of the record).
 */
export function useRowSelectionManager<T extends { id: string }>({
  selectionExcluder,
  getVisibleRows,
}: UseRowSelectionManagerProps<T>): UseRowSelectionManager<T> {
  const [, setSelectedRowsHashState] = useState<Record<string, T>>({});
  const selectedRowsHashRef = useRef<Record<string, T>>({});
  const setSelectedRowsHash = useCallback((updater: any) => {
    setSelectedRowsHashState((prevState) => {
      const nextState =
        typeof updater === "function" ? updater(prevState) : (updater as Record<string, T>);
      selectedRowsHashRef.current = nextState ?? {};
      return selectedRowsHashRef.current;
    });
  }, []);

  const selectRow = useCallback(
    (locator: string, item: T) => {
      if (selectionExcluder && selectionExcluder(item)) {
        return;
      }

      setSelectedRowsHash((prevState) => ({
        ...prevState,
        [locator]: item,
      }));
    },
    [selectionExcluder],
  );

  const deselectRow = useCallback((locator: string): void => {
    setSelectedRowsHash((prevState) => {
      const { [locator]: _, ...rest } = prevState; // Exclude the item with the given locator
      return rest;
    });
  }, []);

  const isRowSelected = useCallback((locator: string): boolean => {
    return locator in selectedRowsHashRef.current;
  }, []);

  const onRowSelectChange = useCallback(
    (locator: string, isSelected: boolean, item: T) => {
      if (isSelected) {
        selectRow(locator, item);
      } else {
        deselectRow(locator);
      }
    },
    [selectRow, deselectRow],
  );

  const selectAllRows = useCallback(
    (rowData: T[]) => {
      for (const row of rowData) {
        selectRow(row["id"], row);
      }
    },
    [selectRow],
  );

  const clearSelectedRows = useCallback(() => {
    setSelectedRowsHash((_prevState) => ({}));
  }, []);

  const setSelectionForVisible = useCallback(
    (selected: boolean) => {
      if (!getVisibleRows) {
        return;
      }

      const visibleRows = getVisibleRows();
      visibleRows.forEach(({ locator, item }) => {
        onRowSelectChange(locator, selected, item);
      });
    },
    [getVisibleRows, onRowSelectChange],
  );

  const areAllVisibleRowsSelected = useCallback(() => {
    if (!getVisibleRows) {
      return false;
    }

    // if some of the visible records aren't selectable, don't consider them when determining if select-all is checked
    return getVisibleRows()
      .filter((r) => (selectionExcluder ? !selectionExcluder(r.item) : true))
      .every((row) => isRowSelected(row.locator));
  }, [getVisibleRows, isRowSelected, selectionExcluder]);

  return useMemo(
    () => ({
      get selectedRowsHash() {
        return selectedRowsHashRef.current;
      },
      setSelectedRowsHash,
      selectRow,
      deselectRow,
      isRowSelected,
      onRowSelectChange,
      selectAllRows,
      clearSelectedRows,
      setSelectionForVisible,
      areAllVisibleRowsSelected,
      get selectedRowCount() {
        return Object.keys(selectedRowsHashRef.current).length;
      },
      get isSelecting() {
        return Object.keys(selectedRowsHashRef.current).length > 0;
      },
      get hasVisibleRows() {
        return (getVisibleRows?.() ?? []).length > 0;
      },
    }),
    [
      setSelectedRowsHash,
      selectRow,
      deselectRow,
      isRowSelected,
      onRowSelectChange,
      selectAllRows,
      clearSelectedRows,
      setSelectionForVisible,
      areAllVisibleRowsSelected,
      getVisibleRows,
    ],
  );
}
